/* ==========================================================================
   RACER — a lane-dodging arcade game for Shopify
   --------------------------------------------------------------------------
   Fixed camera, two to four lanes, traffic coming at you. Pass a car, score a
   point. Hit one and you lose a life.

   THE POINT OF THIS FILE
   Artwork is data, never code. Every sprite is a URL and a frame size handed
   in from the section's settings, and every sprite has a fallback that is
   drawn with rectangles. So:

     - the game is playable and demoable before the client sends a single PNG
     - swapping his art in is uploading files in the theme editor, not a code
       change and not a redeploy
     - if an image 404s or is still uploading, the game keeps running

   That last one matters more than it sounds. A game that breaks because an
   asset is missing is a game that breaks in front of the client.

   ON THE PLACEHOLDER ART
   The cars and scenery drawn below are deliberately decent but not great.
   They exist to prove the mechanic and to keep the game playable while the
   real artwork is drawn. Hand-placed pixel art will beat anything that can
   be expressed as a list of rectangles, which is exactly why the sprites are
   a setting rather than a drawing routine.

   PIXEL ART RULES, both non-negotiable
     1. imageSmoothingEnabled = false, or every sprite arrives blurred.
     2. Scale by whole numbers only. A 28px car drawn at 56 or 84 is crisp;
        drawn at 70 it has uneven pixel widths, which is the exact tell that
        makes fake retro look fake.
   ========================================================================== */

(function () {
  'use strict';

  /* The world is measured in these units and then scaled up by a whole
     number to fill whatever box the section is given. Everything in the game
     thinks in this space, so the maths never cares about screen size. */
  var WORLD_WIDTH = 200;
  var WORLD_HEIGHT = 300;

  var ROAD_LEFT = 36;
  var ROAD_RIGHT = 164;
  var CAR_WIDTH = 32;
  var CAR_HEIGHT = 52;

  /* The background repeats every TILE pixels. Kerb blocks and lane dashes
     both use a 26-on/26-off rhythm, so 52 is the period at which the whole
     roadside pattern lines back up with itself. */
  var TILE = 52;

  /* One place, because tick() uses it as the hitbox and render() uses it as
     the draw position and they must never drift apart.

     Sat 52 units off the bottom rather than 12: the steering buttons are
     overlaid on the lower part of the board now, and a player car underneath
     your own thumb is not a playable game. This also buys a little more
     reaction time, which at the new speed is welcome. */
  var PLAYER_Y = WORLD_HEIGHT - CAR_HEIGHT - 52;

  /* ======================================================================
     DIFFICULTY
     ----------------------------------------------------------------------
     Measured in CAR HEIGHTS OF EMPTY ROAD between one car and the next, not
     in frames. This matters: a fixed frame interval means the gap shrinks
     every time the speed rises, so difficulty ramps twice over and the
     opening is already brutal.

     Spacing in car-heights is what the player actually experiences, and it
     stays honest at any speed:

       spawn interval in frames = gap in pixels / pixels moved per frame

     The opening gap of 4.2 gives roughly three car lengths of clear road at
     the start — enough to see a car, decide, and move. GAP_MIN of 1.55 is about
     as tight as is fair while still leaving room to switch lanes.

     BANDS
     The client asked for named difficulty bands rather than one smooth
     curve, so the gap is interpolated between the anchor points below
     instead of decaying at a flat rate. The bands are deliberately uneven:
     0–10 barely moves, because the first ten cars are where someone decides
     whether this is fun; 20–30 is the steepest, because that is the stretch
     that has to feel like it's costing something to reach the 15% tier.

       score   gap      band
       0       4.20     easy
       10      3.80     moderate
       20      3.15     hard
       30      2.45     very hard
       50      1.75     extreme
       80+     1.55     floor

     Past the last anchor the gap sits at the floor and SPEED keeps climbing
     forever, which is what makes it unbounded without the game ending at an
     arbitrary score — the client was explicit that a good player must be
     allowed to keep going.
     ====================================================================== */
  /* The simulation runs at a fixed 60 steps a second no matter what the
     display does.

     WHY THIS EXISTS: the loop moves the world by `speed` pixels per FRAME.
     On a 120Hz monitor requestAnimationFrame fires twice as often as it does
     on a 60Hz phone, so the same game ran at double speed on desktop. Every
     other duration in here is counted in frames too — spawnIn, invulnerable,
     gapFrames — so switching to per-second maths would mean re-deriving all
     of them.

     An accumulator fixes it without touching any of that: real elapsed time
     goes in, whole 60Hz steps come out, and the frame-counted logic stays
     exactly as tuned. */
  var STEP_MS = 1000 / 60;

  /* A backgrounded tab returns an enormous delta on its first frame back.
     Without a ceiling that becomes hundreds of catch-up steps in one go and
     the player returns to a crash that happened while they were away. */
  var MAX_STEPS = 5;

  var GAP_MIN = 1.55;

  var GAP_CURVE = [
    [0, 4.20],
    [10, 3.80],
    [20, 3.15],
    [30, 2.45],
    [50, 1.75],
    [80, GAP_MIN]
  ];

  /* Four tones per car: body, highlight, shadow, trim. Enough to shade a
     shape without it turning into a gradient. */
  var PALETTES = [
    ['#c4302a', '#e0564c', '#7d1a16', '#f0958a'],
    ['#e09a1e', '#f5bc45', '#8f620d', '#ffdc95'],
    ['#2f6fc4', '#5a9ce8', '#1a3f6e', '#a8cef7'],
    ['#2f9448', '#4fc06c', '#15602c', '#9ae8ae'],
    ['#7d42bd', '#a26bdd', '#4c2374', '#cda8f2'],
    ['#39b7c9', '#63d9e8', '#1c6a77', '#a5eef7']
  ];

  /* Darken a hex colour by a fraction. The dither tones, the road edges and
     the tree shading are all derived from the merchant's chosen colours
     rather than hardcoded, so changing "Verge" in the theme editor moves the
     whole palette together instead of leaving a mismatched dither behind. */
  function shade(hex, amount) {
    var m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
    if (!m) return hex;

    var n = parseInt(m[1], 16);
    var parts = [n >> 16 & 255, n >> 8 & 255, n & 255].map(function (v) {
      return Math.max(0, Math.min(255, Math.round(v * (1 + amount))));
    });

    return '#' + parts.map(function (v) {
      return ('0' + v.toString(16)).slice(-2);
    }).join('');
  }

  /* ======================================================================
     SOUND
     ----------------------------------------------------------------------
     OFF BY DEFAULT, AND THAT IS DELIBERATE.

     This game sits on a shop page. A page that starts making engine noise
     because someone scrolled past it is a page people close. So sound is
     opt-in twice over: the merchant has to enable it in settings, and the
     shopper still gets a mute button that remembers their choice.

     Browsers also refuse to play audio until the user has interacted with
     the page, which suits us — the engine only ever starts on a tap or a key
     press, which is the interaction the browser is waiting for.

     One-shots keep a small pool of copies. Without it, changing lanes twice
     quickly cuts the first sound off mid-way and it reads as a glitch.
     ====================================================================== */

  /* ======================================================================
     SYNTHESISED PLACEHOLDER AUDIO
     ----------------------------------------------------------------------
     The same bargain the placeholder cars make: every sound has a version
     generated in code, so the game is fully demoable before anyone sources
     a single audio file, and dropping a real clip in later is pasting a URL
     rather than a code change.

     Why generate rather than ship files:
       - nothing to license, and nothing to get an Instagram reel muted
       - no bytes on a gate page that is somebody's first impression
       - no 404 to break the game if a file goes missing

     The AudioContext is created lazily on first use, never on load. Browsers
     start one suspended until a user gesture, and a suspended context left
     lying around is a console warning on every page view.
     ====================================================================== */
  function Synth() {
    this.ctx = null;
    this.sfx = null;
    this.music = null;
    this.engine = null;
    this.timer = null;
    this.step = 0;
    this.nextNoteAt = 0;
  }

  Synth.prototype.ready = function () {
    if (this.ctx) {
      /* Safari suspends the context when the tab is backgrounded and does
         not resume it on its own. */
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return true;
    }

    var Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return false;

    try {
      this.ctx = new Ctor();
    } catch (error) {
      return false;
    }

    this.sfx = this.ctx.createGain();
    this.sfx.connect(this.ctx.destination);

    this.music = this.ctx.createGain();
    this.music.connect(this.ctx.destination);

    return true;
  };

  Synth.prototype.levels = function (sfx, music) {
    if (!this.ready()) return;
    this.sfx.gain.value = sfx;
    this.music.gain.value = music;
  };

  /* One note. Everything audible here is built out of this: an oscillator, a
     gain envelope, and a stop time — no sample, no buffer. */
  Synth.prototype.tone = function (opts) {
    if (!this.ready()) return;

    var now = this.ctx.currentTime + (opts.at || 0);
    var osc = this.ctx.createOscillator();
    var gain = this.ctx.createGain();

    osc.type = opts.type || 'square';
    osc.frequency.setValueAtTime(opts.from, now);

    /* A sweep is what separates a blip from a beep. */
    if (opts.to && opts.to !== opts.from) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.to), now + opts.length);
    }

    /* Ramp rather than set: a gain that jumps to zero clicks, and a click on
       every lane change is the fastest way to make audio feel broken. */
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(opts.level || 0.2, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + opts.length);

    osc.connect(gain);
    gain.connect(opts.bus === 'music' ? this.music : this.sfx);

    osc.start(now);
    osc.stop(now + opts.length + 0.02);
  };

  /* White noise through a filter — the basis of the crash and the hi-hat. */
  Synth.prototype.noise = function (opts) {
    if (!this.ready()) return;

    var now = this.ctx.currentTime + (opts.at || 0);
    var frames = Math.floor(this.ctx.sampleRate * opts.length);
    var buffer = this.ctx.createBuffer(1, frames, this.ctx.sampleRate);
    var data = buffer.getChannelData(0);

    for (var i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;

    var source = this.ctx.createBufferSource();
    source.buffer = buffer;

    var filter = this.ctx.createBiquadFilter();
    filter.type = opts.filter || 'lowpass';
    filter.frequency.setValueAtTime(opts.cutoff || 1200, now);

    var gain = this.ctx.createGain();
    gain.gain.setValueAtTime(opts.level || 0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + opts.length);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(opts.bus === 'music' ? this.music : this.sfx);

    source.start(now);
  };

  /* --- the four effects ------------------------------------------------ */

  Synth.prototype.drift = function () {
    /* Up, not down: a rising blip reads as a deliberate action, a falling
       one reads as a mistake. */
    this.tone({ type: 'square', from: 420, to: 780, length: 0.12, level: 0.16 });
  };

  Synth.prototype.crash = function () {
    this.noise({ length: 0.45, cutoff: 900, level: 0.35 });
    /* The low sweep underneath is what gives it weight; noise alone reads
       as static rather than impact. */
    this.tone({ type: 'sawtooth', from: 180, to: 40, length: 0.5, level: 0.3 });
  };

  Synth.prototype.reward = function () {
    /* A major arpeggio, the one unambiguous "you won" gesture in games. */
    var notes = [523.25, 659.25, 783.99, 1046.5];
    for (var i = 0; i < notes.length; i++) {
      this.tone({
        type: 'square',
        from: notes[i],
        to: notes[i],
        length: 0.16,
        level: 0.18,
        at: i * 0.085
      });
    }
  };

  Synth.prototype.engineStart = function () {
    if (!this.ready() || this.engine) return;

    var osc = this.ctx.createOscillator();
    var gain = this.ctx.createGain();
    var filter = this.ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.value = 62;

    /* Rolled right off. An unfiltered sawtooth drone is genuinely unpleasant
       after ten seconds, and this one is meant to run for minutes. */
    filter.type = 'lowpass';
    filter.frequency.value = 220;

    /* Quieter than it was. This is the only sound playing continuously, so
       it sets the noise floor for everything else — the lane-change blip and
       the music both have to cut through it. */
    gain.gain.value = 0.035;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfx);
    osc.start();

    this.engine = { osc: osc, gain: gain, filter: filter };
  };

  /* Throttle, 0 at the starting speed and 1 flat out. Pitch and brightness
     both climb, because an engine winding up does both — raising pitch alone
     sounds like a tape speeding up rather than a car accelerating.

     Ramped over 180ms rather than set: stepping the frequency clicks, and a
     click on every point scored is worse than no engine at all. */
  Synth.prototype.engineRate = function (t) {
    if (!this.engine || !this.ctx) return;

    var level = Math.max(0, Math.min(1, t));
    var now = this.ctx.currentTime;

    this.engine.osc.frequency.linearRampToValueAtTime(62 + level * 78, now + 0.18);
    this.engine.filter.frequency.linearRampToValueAtTime(220 + level * 900, now + 0.18);
    this.engine.gain.gain.linearRampToValueAtTime(0.03 + level * 0.03, now + 0.18);
  };

  Synth.prototype.engineStop = function () {
    if (!this.engine) return;
    try {
      this.engine.osc.stop();
    } catch (error) {
      /* Already stopped. */
    }
    this.engine = null;
  };

  /* --- the placeholder soundtrack -------------------------------------- */

  /* ----------------------------------------------------------------------
     THE CHIPTUNE

     Two sections of A minor at 140bpm in eighth notes, 64 steps, about
     fourteen seconds before it repeats. Section A is bass and arpeggio;
     section B adds a lead over the same harmony, which is what stops a loop
     this short announcing itself on the third pass.

     Four voices, which is roughly what an NES had to work with, and the
     reason this reads as 8-bit rather than as "synthesiser": square and
     triangle waves only, no filters sweeping about, drums made of noise.
     ---------------------------------------------------------------------- */

  /* A2 G2 F2 E2, two bars each, with octave jumps in the second half. */
  var MUSIC_BASS = [
    45, 45, 57, 45, 43, 43, 55, 43, 41, 41, 53, 41, 40, 40, 52, 40,
    45, 45, 57, 45, 43, 43, 55, 43, 41, 41, 53, 41, 40, 47, 52, 47,
    45, 45, 57, 45, 43, 43, 55, 43, 41, 41, 53, 41, 40, 40, 52, 40,
    45, 45, 57, 45, 43, 43, 55, 43, 41, 41, 53, 41, 40, 47, 52, 47
  ];

  var MUSIC_ARP = [
    69, 72, 76, 72, 67, 71, 74, 71, 65, 69, 72, 69, 64, 68, 71, 68,
    76, 72, 69, 72, 74, 71, 67, 71, 72, 69, 65, 69, 71, 68, 64, 68,
    69, 72, 76, 72, 67, 71, 74, 71, 65, 69, 72, 69, 64, 68, 71, 68,
    76, 72, 69, 72, 74, 71, 67, 71, 72, 69, 65, 69, 71, 68, 64, 68
  ];

  /* 0 is a rest. Silent through section A so the lead's entry is an event. */
  var MUSIC_LEAD = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    81, 0, 79, 0, 76, 0, 79, 81, 79, 0, 76, 0, 74, 0, 0, 0,
    77, 0, 76, 0, 72, 0, 76, 77, 76, 0, 72, 0, 71, 0, 69, 0
  ];

  var MUSIC_STEP = 60 / 140 / 2;

  function midi(note) {
    return 440 * Math.pow(2, (note - 69) / 12);
  }

  /* Sine dropping fast. A kick is a pitch envelope, not a sample. */
  Synth.prototype.kick = function (at) {
    this.tone({
      type: 'sine', bus: 'music', at: at,
      from: 130, to: 45, length: 0.13, level: 0.5
    });
  };

  Synth.prototype.snare = function (at) {
    this.noise({
      bus: 'music', at: at, length: 0.1,
      filter: 'highpass', cutoff: 1800, level: 0.16
    });
  };

  Synth.prototype.musicStart = function () {
    if (!this.ready() || this.timer) return;

    var self = this;
    this.step = 0;
    this.nextNoteAt = this.ctx.currentTime + 0.05;

    /* Lookahead scheduling. setInterval alone is far too jittery to place
       notes on — the trick is to let the timer run loosely and schedule each
       note against the audio clock, which is sample accurate. */
    this.timer = window.setInterval(function () {
      if (!self.ctx) return;

      while (self.nextNoteAt < self.ctx.currentTime + 0.15) {
        var at = self.nextNoteAt - self.ctx.currentTime;
        var i = self.step % MUSIC_BASS.length;
        var inBar = i % 8;

        self.tone({
          type: 'triangle', bus: 'music', at: at,
          from: midi(MUSIC_BASS[i]), to: midi(MUSIC_BASS[i]),
          length: MUSIC_STEP * 0.9, level: 0.26
        });

        self.tone({
          type: 'square', bus: 'music', at: at,
          from: midi(MUSIC_ARP[i]), to: midi(MUSIC_ARP[i]),
          length: MUSIC_STEP * 0.5, level: 0.07
        });

        if (MUSIC_LEAD[i]) {
          self.tone({
            type: 'square', bus: 'music', at: at,
            from: midi(MUSIC_LEAD[i]), to: midi(MUSIC_LEAD[i]),
            length: MUSIC_STEP * 1.6, level: 0.13
          });
        }

        if (inBar === 0 || inBar === 4) self.kick(at);
        if (inBar === 2 || inBar === 6) self.snare(at);
        if (inBar % 2 === 1) {
          self.noise({
            bus: 'music', at: at, length: 0.035,
            filter: 'highpass', cutoff: 7000, level: 0.045
          });
        }

        self.nextNoteAt += MUSIC_STEP;
        self.step += 1;
      }
    }, 25);
  };

  Synth.prototype.musicStop = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  function Sound(root, enabled, volume) {
    this.enabled = enabled;
    this.volume = Math.max(0, Math.min(1, volume));
    this.clips = {};
    /* No mute UI: the device has a volume control and the game is opened by
       a deliberate tap, so audio here is expected rather than ambushing
       anyone. Kept as a field because the visibility handler still silences
       everything when the tab goes away. */
    this.muted = false;

    /* Every effect falls back to a generated one, exactly as every sprite
       falls back to a drawn one. A merchant who pastes a URL overrides the
       placeholder for that one sound; the rest keep working. */
    this.synth = new Synth();

    if (!this.enabled) return;

    this.add('engine', root.getAttribute('data-racer-sound-engine'), true);

    /* Optional four-stage engine. Give it idle/low/mid/max recordings and the
       game switches between them as the speed climbs; give it one clip, or
       none, and it falls back to that clip or to the synth. The bands exist
       so ANY set of licensed recordings can be dropped in — nothing here
       assumes a particular source. */
    this.add('engine_low', root.getAttribute('data-racer-sound-engine-low'), true);
    this.add('engine_med', root.getAttribute('data-racer-sound-engine-med'), true);
    this.add('engine_high', root.getAttribute('data-racer-sound-engine-high'), true);
    this.add('engine_max', root.getAttribute('data-racer-sound-engine-max'), true);
    this.bands = ['engine_low', 'engine_med', 'engine_high', 'engine_max'];
    this.band = -1;
    this.add('drift', root.getAttribute('data-racer-sound-drift'), false);
    this.add('crash', root.getAttribute('data-racer-sound-crash'), false);
    this.add('reward', root.getAttribute('data-racer-sound-reward'), false);

    /* Background music is a loop like the engine, but quieter by default and
       on its own level — music at effects volume buries the effects, and the
       lane-change blip is the one piece of feedback the player actually
       needs to hear. */
    this.add('music', root.getAttribute('data-racer-sound-music'), true);

    /* Music volume is RELATIVE to the main volume, not independent of it.

       It used to be its own absolute level, which made the main Volume
       setting effectively an effects-only control — and since the engine is
       a continuous drone on the effects bus, dropping music to 10% changed
       almost nothing you could hear. Volume is now the master and this is
       the music's share of it, which is how both sliders read on the tin. */
    var musicLevel = parseInt(root.getAttribute('data-racer-music-volume'), 10);
    var musicShare = isNaN(musicLevel) ? 0.5 : Math.max(0, Math.min(1, musicLevel / 100));
    this.musicVolume = this.volume * musicShare;
  }

  Sound.prototype.add = function (name, url, loop) {
    if (!url) return;

    var make = function () {
      var audio = new Audio();
      audio.preload = 'auto';
      audio.loop = !!loop;
      audio.src = url;
      return audio;
    };

    /* A loop needs one element; a one-shot needs a few so they can overlap. */
    this.clips[name] = {
      pool: loop ? [make()] : [make(), make(), make()],
      next: 0,
      loop: !!loop
    };
  };

  Sound.prototype.has = function () {
    for (var key in this.clips) {
      if (Object.prototype.hasOwnProperty.call(this.clips, key)) return true;
    }
    return false;
  };

  /* Called as the speed changes. Switches the looping clip when the band
     changes, or moves the synth's throttle when there are no clips. */
  Sound.prototype.throttle = function (t) {
    if (!this.enabled || this.muted) return;

    this.synth.levels(this.volume, this.musicVolume);

    var staged = this.bands && this.bands.some(function (name) {
      return !!this.clips[name];
    }, this);

    if (!staged) {
      this.synth.engineRate(t);
      return;
    }

    var index = Math.max(0, Math.min(3, Math.floor(t * 4)));
    if (index === this.band) return;

    /* Walk down to the nearest band that actually has a clip, so a merchant
       who uploads only idle and max still gets both rather than silence
       wherever a slot is empty. */
    var pick = index;
    while (pick > 0 && !this.clips[this.bands[pick]]) pick -= 1;
    if (!this.clips[this.bands[pick]]) return;

    if (this.band >= 0) this.stop(this.bands[this.band]);
    this.band = index;
    this.play(this.bands[pick]);
  };

  Sound.prototype.stopEngine = function () {
    this.stop('engine');
    this.synth.engineStop();

    if (this.band >= 0 && this.bands) {
      this.stop(this.bands[Math.max(0, Math.min(3, this.band))]);
      this.band = -1;
    }
  };

  Sound.prototype.levelFor = function (name) {
    return name === 'music' ? this.musicVolume : this.volume;
  };

  Sound.prototype.play = function (name) {
    if (!this.enabled || this.muted) return;

    var clip = this.clips[name];

    /* No uploaded clip for this one: generate it. */
    if (!clip) {
      this.synth.levels(this.volume, this.musicVolume);

      if (name === 'drift') this.synth.drift();
      else if (name === 'crash') this.synth.crash();
      else if (name === 'reward') this.synth.reward();
      else if (name === 'engine') this.synth.engineStart();
      else if (name === 'music') this.synth.musicStart();

      return;
    }

    var audio = clip.pool[clip.next];
    clip.next = (clip.next + 1) % clip.pool.length;

    audio.volume = this.levelFor(name);

    /* Restarting a loop that is already playing would stutter the music on
       every new run. One-shots always restart, which is the point of them. */
    if (!clip.loop || audio.paused) audio.currentTime = 0;

    /* play() rejects when the browser has not seen an interaction yet, and
       an unhandled rejection is a console error on a shop page. Swallow it:
       a missing sound effect is not worth a broken-looking site. */
    var attempt = audio.play();
    if (attempt && attempt.catch) attempt.catch(function () {});
  };

  Sound.prototype.stop = function (name) {
    if (!this.clips[name]) {
      if (name === 'engine') this.synth.engineStop();
      if (name === 'music') this.synth.musicStop();
      return;
    }

    var clip = this.clips[name];
    if (!clip) return;

    for (var i = 0; i < clip.pool.length; i++) {
      clip.pool[i].pause();
      clip.pool[i].currentTime = 0;
    }
  };

  /* ======================================================================
     THE REWARD CODE, AND AN HONEST NOTE ABOUT IT
     ----------------------------------------------------------------------
     The discount code is a theme setting, which means it is delivered to the
     browser with the page. It is base64-encoded here so that it is not
     sitting in the HTML as readable text — that stops someone spotting it
     by eye or by searching the source for "SAVE".

     It is a speed bump, not a lock, and it must not be sold as one. Anyone
     who opens dev tools and reads this file can decode it. Genuinely
     unguessable codes need a server issuing one code per player, which is a
     different and much larger piece of work.

     The right protection is on Shopify's side: set a usage limit on the
     discount. Then it does not matter if the code escapes — it stops working
     after the number of redemptions the merchant is happy to give away. Set
     that limit before the game goes live, not after.
     ====================================================================== */
  function decodeCode(raw) {
    if (!raw) return '';
    try {
      return window.atob(raw);
    } catch (error) {
      /* Not valid base64 — treat it as already plain and carry on, so a
         hand-edited setting still works rather than silently vanishing. */
      return raw;
    }
  }

  /* A filled circle drawn as whole-pixel rows. Canvas arcs anti-alias, which
     is exactly what pixel art must not do — a tree drawn with arc() has a
     soft grey fringe that reads as a JPEG artefact once the canvas is scaled
     up. Stacking integer-width rows gives the hard stepped edge instead. */
  function pixelCircle(ctx, cx, cy, r) {
    ctx.beginPath();
    for (var y = -r; y <= r; y++) {
      var half = Math.round(Math.sqrt(r * r - y * y));
      if (half > 0) ctx.rect(Math.round(cx - half), Math.round(cy + y), half * 2, 1);
    }
    ctx.fill();
  }

  /* ======================================================================
     SPRITES
     ----------------------------------------------------------------------
     A sheet is one image with frames laid out left to right. Frame size
     comes from the settings, so any grid the client draws on works without
     touching this file.
     ====================================================================== */

  /* frameCount, not frame width.

     Frame WIDTH is a number of source pixels, and Shopify's CDN changes the
     source pixels — it serves the traffic sheet capped at 1024 wide. Export
     four cars at 640 each, enter 640, and the delivered file is 1024 wide
     with 256px frames: the game computes one frame and draws a slice of the
     first car. The setting would be right and the game would still be
     wrong.

     A frame COUNT survives resizing. Four cars is four cars at any
     delivered width, so the frame width is derived from the file that
     actually arrives. */
  function Sheet(url, frameCount, single, onReady) {
    this.ready = false;
    this.frameWidth = CAR_WIDTH;
    this.frameHeight = CAR_HEIGHT;
    this.frames = 1;
    this.wanted = Math.max(1, frameCount || 1);
    this.single = !!single;

    if (!url) return;

    var self = this;
    this.image = new Image();

    this.image.onload = function () {
      self.ready = true;

      /* The board is painted once before any of this has downloaded, so
         without a nudge the idle screen keeps showing the placeholder car
         until the player starts a run. Which looks exactly like the upload
         not working — and that is the first thing anyone checks after
         uploading their artwork. */
      if (onReady) {
        window.setTimeout(function () {
          onReady();
        }, 0);
      }

      /* Sliced by count, so the delivered width is whatever it is. */
      var count = self.single ? 1 : self.wanted;

      self.frames = count;
      self.frameWidth = self.image.width / count;
      self.frameHeight = self.image.height;
    };

    /* No error handling beyond leaving ready false — the fallback draws and
       nobody sees a broken game. */
    this.image.src = url;
  }

  /* Draws the frame INSIDE the given box without distorting it.

     The box is the game's idea of a car — 32 x 52. Artwork almost never
     arrives at exactly that ratio, and the old code stretched it to fit,
     which is why a slightly wide car came out visibly squashed. Now the
     frame is scaled until it fits and centred in the box, so a 3:4 car and
     a 32:52 car both look like themselves; the only difference is how much
     of the box each one fills.

     Fitting rather than filling also means a stray aspect ratio can never
     make a car overhang its lane. */
  /* The height this frame will actually occupy inside the given box.

     Not the same as the box height: fitting preserves aspect, so a frame
     narrower than the box fills it vertically, while a wider one leaves gaps
     top and bottom. Collision needs the real number, not the box, or a car
     with letterboxing above and below would crash into thin air. */
  Sheet.prototype.fittedHeight = function (width, height) {
    if (!this.ready) return height;

    var srcRatio = this.frameWidth / this.frameHeight;
    var boxRatio = width / height;

    return srcRatio > boxRatio ? width / srcRatio : height;
  };

  Sheet.prototype.draw = function (ctx, frame, x, y, width, height) {
    if (!this.ready) return false;

    var index = Math.max(0, Math.min(this.frames - 1, frame | 0));

    var srcRatio = this.frameWidth / this.frameHeight;
    var boxRatio = width / height;

    var w = width;
    var h = height;

    if (srcRatio > boxRatio) {
      /* Wider than the box: width decides, height follows. */
      h = width / srcRatio;
    } else {
      w = height * srcRatio;
    }

    ctx.drawImage(
      this.image,
      index * this.frameWidth, 0, this.frameWidth, this.frameHeight,
      x + (width - w) / 2, y + (height - h) / 2, w, h
    );
    return true;
  };

  /* ======================================================================
     GAME
     ====================================================================== */

  function Racer(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-racer-canvas]');
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');

    /* RESOLUTION
       ----------------------------------------------------------------
       The game thinks in a 200x300 world. That used to be the canvas size
       too, which capped everything at 200x300 real pixels — fine for pixel
       art, hopeless for detailed artwork, which arrived downsampled to
       mush no matter how large the source file was.

       The backing store is now scaled up and the context scaled to match,
       so every drawing routine still works in world units and nothing else
       in this file had to change — but a 32-unit car is rendered into
       32 x scale real pixels. At scale 4 that is a 128px car on a
       800x1200 canvas. */
    var scale = Math.max(1, Math.min(6, parseInt(root.getAttribute('data-racer-render-scale'), 10) || 1));
    this.scale = scale;

    this.canvas.width = WORLD_WIDTH * scale;
    this.canvas.height = WORLD_HEIGHT * scale;
    this.ctx.setTransform(scale, 0, 0, scale, 0, 0);

    /* Smoothing OFF is what makes pixel art crisp and what makes detailed
       artwork look like a bad JPEG. It follows the art style setting. */
    this.pixelArt = root.getAttribute('data-racer-pixel-art') !== 'false';

    /* SPRITE SCALE
       ----------------------------------------------------------------
       Artwork almost never arrives filling its frame edge to edge — a car
       drawn with a bit of transparent margin renders smaller than the lane
       and looks wrong next to everything else. Rather than send the file
       back to be re-cropped, this scales the DRAWN size up or down.

       The HITBOX does not change. Collision stays at 32x52 whatever this
       is set to, so making a car look bigger never makes it harder to
       play — which would be an unfair and completely invisible bug. */
    this.playerScale = (parseInt(root.getAttribute('data-racer-player-scale'), 10) || 100) / 100;
    this.enemyScale = (parseInt(root.getAttribute('data-racer-enemy-scale'), 10) || 100) / 100;

    /* COLLISION FOLLOWS THE DRAWN CAR.

       The hitbox used to be a fixed 32x52 whatever the sprites were scaled
       to. At 130% that means the car you can see is wider and taller than
       the car the game tests, so a visible clip registers as a clean pass
       and a near miss registers as a hit. Either way the player is right
       and the game is wrong, which is the worst kind of bug in an arcade
       game: invisible, and it feels like cheating.

       These are the half-extents of what is actually on screen. */
    this.playerBoxW = CAR_WIDTH * this.playerScale;
    this.playerBoxH = CAR_HEIGHT * this.playerScale;
    this.enemyBoxW = CAR_WIDTH * this.enemyScale;
    this.enemyBoxH = CAR_HEIGHT * this.enemyScale;
    this.ctx.imageSmoothingEnabled = !this.pixelArt;
    if (!this.pixelArt) this.ctx.imageSmoothingQuality = 'high';
    this.root.classList.toggle('racer--smooth', !this.pixelArt);

    var read = function (name, fallback) {
      var value = parseInt(root.getAttribute('data-racer-' + name), 10);
      return isNaN(value) ? fallback : value;
    };

    this.lanes = Math.max(2, Math.min(4, read('lanes', 3)));
    this.lives = read('lives', 3);
    /* Divisor tuned so the default 60 starts at a speed you can read the
       road at. It climbs from here via rampEvery. */
    this.baseSpeed = read('speed', 60) / 55;
    this.rampEvery = read('ramp', 10);

    this.colors = {
      road: root.getAttribute('data-racer-road-color') || '#3f3f45',
      grass: root.getAttribute('data-racer-grass-color') || '#2b8038',
      line: root.getAttribute('data-racer-line-color') || '#e8e8e8',
      player: root.getAttribute('data-racer-player-color') || '#c8342a'
    };

    /* Repaints the idle board when a sprite finishes downloading. Guarded on
       background, because a cached image can resolve while the constructor
       is still assembling the pieces render() needs. */
    var self = this;
    var repaint = function () {
      if (self.background && !self.running) self.render();
    };


    /* The player car is one image, never a sheet — there is only ever one of
       them on screen, so slicing it by a frame width can only go wrong. */
    this.player = new Sheet(root.getAttribute('data-racer-player-src'), 1, true, repaint);
    this.enemy = new Sheet(root.getAttribute('data-racer-enemy-src'), read('enemy-frames', 1), false, repaint);

    /* Scenery is optional hand-drawn art in place of the generated trees.
       Same sheet convention as the cars: frames side by side. */
    this.scenerySheet = new Sheet(
      root.getAttribute('data-racer-scenery-src'),
      read('scenery-frames', 1),
      false,
      repaint
    );

    /* Scenery is drawn into a box measured in WORLD units, like the cars.
       Its frame size is whatever the delivered file happens to be, and
       drawing a 256px frame at 256 world units would put a tree taller than
       the screen on the verge. */
    this.sceneryBox = Math.max(4, read('scenery-size', 28));

    this.goal = read('goal', 0);
    this.rewardCode = decodeCode(root.getAttribute('data-racer-reward-code'));

    /* ONE reward, not a ladder.

       This started as three tiers with a code each. It collapsed to a single
       goal because the codes now come from Klaviyo — and Klaviyo issues one
       unique code per profile per coupon, so three tiers would have meant
       three coupons, three forms, and a way for someone to claim twice by
       replaying into a higher tier. One goal has none of those problems.

       The retry nudge survives the simplification intact: "12 more dodges to
       unlock 15% off" is the same mechanic, just counting to one number. */
    this.rewardScore = read('reward-score', 50);
    this.rewardPercent = read('reward-percent', 15);
    this.carGodScore = read('cargod-score', 0);

    this.result = {
      tier: root.querySelector('[data-racer-tier]'),
      carGod: root.querySelector('[data-racer-cargod]'),
      nudge: root.querySelector('[data-racer-nudge]'),
      early: root.querySelector('[data-racer-early]'),
      claim: root.querySelector('[data-racer-claim]'),
      form: root.querySelector('[data-racer-claim-form]')
    };

    this.reward = {
      panel: root.querySelector('[data-racer-reward]'),
      code: root.querySelector('[data-racer-reward-code-out]'),
      copy: root.querySelector('[data-racer-reward-copy]'),
      dismiss: root.querySelector('[data-racer-reward-dismiss]')
    };

    this.rewarded = false;

    this.sound = new Sound(
      root,
      root.getAttribute('data-racer-sound') === 'true',
      read('volume', 60) / 100
    );


    this.panel = {
      root: root.querySelector('[data-racer-over]'),
      score: root.querySelector('[data-racer-over-score]'),
      best: root.querySelector('[data-racer-over-best]'),
      record: root.querySelector('[data-racer-over-record]'),
      again: root.querySelector('[data-racer-again]')
    };

    this.over = false;

    this.hud = {
      score: root.querySelector('[data-racer-score]'),
      best: root.querySelector('[data-racer-best]'),
      lives: root.querySelector('[data-racer-lives]'),
      status: root.querySelector('[data-racer-status]')
    };

    this.startButton = root.querySelector('[data-racer-start]');
    this.idle = root.querySelector('[data-racer-idle]');
    this.frame = null;
    this.running = false;

    this.buildBackground();
    this.loadRoadStrip(root.getAttribute('data-racer-road-src'));

    this.best = this.loadBest();
    this.bind();
    this.reset();

    /* Someone who earned the code yesterday and came back today must not
       have to earn it again to read it. Losing a discount code to a page
       refresh is the kind of thing that generates support email. */
    if (this.goal > 0 && this.rewardCode && this.remembered()) this.revealReward();
  }

  /* The road art, if the artist drew it: one PNG the full width of the
     playfield that tiles vertically. Its own height becomes the scroll
     period, so a 64px strip and a 200px strip both just work. */
  Racer.prototype.loadRoadStrip = function (url) {
    if (!url) return;

    var self = this;
    var image = new Image();

    image.onload = function () {
      if (!image.width || !image.height) return;

      self.background = image;

      /* Converted to WORLD units, not taken as pixels.

         The strip spans the full 200-unit width whatever its file size, so
         its height in world units is its pixel height scaled by the same
         factor. Reading image.height directly worked only back when the
         canvas was 200x300 and strips were 200px wide; a 1600x832 strip
         would otherwise claim to be 832 units tall — nearly three screens —
         and scroll about once a minute. */
      self.tileHeight = image.height * (WORLD_WIDTH / image.width);

      self.render();
    };

    /* No onerror handling on purpose: a failed load simply leaves the
       generated road in place and the game carries on. */
    image.src = url;
  };

  Racer.prototype.laneX = function (lane) {
    var width = (ROAD_RIGHT - ROAD_LEFT) / this.lanes;
    return ROAD_LEFT + width * lane + (width - CAR_WIDTH) / 2;
  };

  Racer.prototype.loadBest = function () {
    try {
      return parseInt(window.localStorage.getItem('racer:best'), 10) || 0;
    } catch (error) {
      return 0;
    }
  };

  Racer.prototype.saveBest = function () {
    try {
      window.localStorage.setItem('racer:best', String(this.best));
    } catch (error) {
      /* Private mode. A remembered high score is a nicety, not a feature. */
    }
  };

  Racer.prototype.reset = function () {
    if (this.idle) this.idle.hidden = false;

    this.over = false;
    if (this.panel.root) this.panel.root.hidden = true;

    this.lane = Math.floor(this.lanes / 2);
    this.traffic = [];
    this.score = 0;
    this.livesLeft = this.lives;
    this.speed = this.baseSpeed;
    this.scroll = 0;
    this.invulnerable = 0;

    /* A full gap before the first car, plus a beat. Opening on a car already
       halfway down the screen is the single fastest way to make a game feel
       unfair, and it is what "I'm losing before I've started" means. */
    this.spawnIn = this.gapFrames() + 30;

    /* Roadside dressing, scattered once and scrolled forever. Trees are what
       stop the verge reading as a flat green bar.

       Alternating sides rather than a coin flip per item: random sides clump,
       and a run of six trees on the left with a bare right verge looks like a
       bug even though it is correct. */
    this.scenery = [];
    for (var i = 0; i < 16; i++) {
      var left = i % 2 === 0;
      this.scenery.push({
        x: left ? 4 + Math.random() * 20 : ROAD_RIGHT + 14 + Math.random() * 20,
        y: Math.random() * WORLD_HEIGHT,
        r: 7 + Math.random() * 6,
        kind: Math.random() < 0.62 ? 'tree' : 'bush',
        frame: Math.floor(Math.random() * Math.max(1, this.scenerySheet.frames))
      });
    }

    this.report();
    this.render();
  };

  Racer.prototype.report = function () {
    if (this.hud.score) this.hud.score.textContent = String(this.score);
    if (this.hud.best) this.hud.best.textContent = String(this.best);
    if (this.hud.lives) this.hud.lives.textContent = String(Math.max(0, this.livesLeft));
  };

  Racer.prototype.status = function (key) {
    if (!this.hud.status) return;
    this.hud.status.textContent =
      this.root.getAttribute('data-racer-text-' + key) || '';
  };

  /* ---------------------------------------------------------------- input */

  Racer.prototype.move = function (direction) {
    /* Steering starts a run that has not begun, but never restarts one that
       has ended. After a game over the player gets to read their score and
       decide — a stray tap must not throw them into a new run before they
       have seen how they did. */
    if (this.over) return;
    if (!this.running) this.start();

    var next = this.lane + direction;

    /* Only when the car actually moves. Steering into the barrier and
       hearing a tyre squeal is worse than silence. */
    if (next < 0 || next >= this.lanes) return;

    this.lane = next;
    this.sound.play('drift');
  };

  Racer.prototype.bind = function () {
    var self = this;

    document.addEventListener('keydown', function (event) {
      if (!self.onScreen()) return;

      var left = event.key === 'ArrowLeft' || event.key === 'a' || event.key === 'A';
      var right = event.key === 'ArrowRight' || event.key === 'd' || event.key === 'D';
      if (!left && !right) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      var tag = (event.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      event.preventDefault();
      self.move(left ? -1 : 1);
    });

    /* Tapping the road used to steer. It went, because on a phone the thumb
       lands on the lower half of the screen — over the car — and a tap that
       is meant as "go left" reads as ambiguous the moment it lands anywhere
       near the middle. The arrow buttons are unambiguous, and they are now
       the only pointer control. Keyboard is unaffected. */

    this.root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-racer-move]');
      if (button) self.move(button.getAttribute('data-racer-move') === 'left' ? -1 : 1);
    });

    if (this.panel.again) {
      this.panel.again.addEventListener('click', function () {
        self.stop();
        self.reset();
        self.start();
      });
    }

    if (this.startButton) {
      this.startButton.addEventListener('click', function () {
        /* Paused mid-run because they just hit the goal: resume rather than
           restart. Wiping a good run as the reward for succeeding at it is
           the wrong lesson.

           The !self.over guard is load-bearing: without it, a player who
           earned the code and then died would find "Play again" resuming a
           dead run instead of starting a new one. */
        if (!self.over && self.rewarded && !self.running &&
            self.livesLeft > 0 && self.score > 0) {
          self.start();
          return;
        }

        self.stop();
        self.reset();
        self.start();
      });
    }

    /* Leaving the tab must not leave an engine idling in the background. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
    });

    if (this.reward.copy) {
      this.reward.copy.addEventListener('click', function () {
        var done = function () {
          var was = self.reward.copy.textContent;
          self.reward.copy.textContent =
            self.root.getAttribute('data-racer-text-copied') || 'Copied';

          window.setTimeout(function () {
            self.reward.copy.textContent = was;
          }, 1600);
        };

        /* The clipboard API needs a secure context and a permission that can
           be refused. select-the-text is the fallback, because a code you
           cannot copy is a code nobody redeems. */
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(self.rewardCode).then(done, function () {
            self.selectCode();
          });
        } else {
          self.selectCode();
        }
      });
    }

    if (this.reward.dismiss) {
      this.reward.dismiss.addEventListener('click', function () {
        if (self.reward.panel) self.reward.panel.hidden = true;
      });
    }

    /* ------------------------------------------------------ claim flow */

    if (this.result.claim) {
      this.result.claim.addEventListener('click', function () {
        self.show(self.result.claim, false);
        self.show(self.result.form, true);

        /* Klaviyo renders its form lazily into the container. Nudging it
           after the container becomes visible avoids the case where the form
           was measured at zero height while hidden and renders collapsed. */
        if (window._klOnsite) {
          window._klOnsite = window._klOnsite || [];
          try {
            window._klOnsite.push(['openForm', '']);
          } catch (error) {
            /* Klaviyo not loaded on this page; the container stays empty and
               the merchant sees that in the editor rather than a shopper
               seeing it live. */
          }
        }
      });
    }

    /* A loop running behind a hidden tab drains a battery for nothing. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
    });
  };

  Racer.prototype.onScreen = function () {
    return !!(this.root.offsetParent || this.root.getClientRects().length);
  };

  /* ------------------------------------------------------------ lifecycle */

  Racer.prototype.start = function () {
    /* The hint is the start control now that the button is gone, so it goes
       the moment a run begins and comes back with reset(). */
    if (this.idle) this.idle.hidden = true;

    if (this.running || this.over) return;

    this.running = true;
    this.status('playing');
    this.sound.play('engine');
    this.reportThrottle();

    /* Started here rather than on load, because a browser will not play
       audio until the user has interacted with the page — and because a
       page that starts a soundtrack unprompted gets closed. */
    this.sound.play('music');

    if (this.startButton) {
      this.startButton.textContent =
        this.root.getAttribute('data-racer-text-restart') || 'Restart';
    }

    var self = this;

    /* Cleared so a resume does not bank the time spent paused. */
    this.lastFrame = 0;
    this.accumulator = 0;

    var step = function (now) {
      /* Checked here, not only where it is set: without this the loop keeps
         running after a game over and lives march into the negatives. */
      if (!self.running) return;

      if (!self.lastFrame) self.lastFrame = now;
      var delta = now - self.lastFrame;
      self.lastFrame = now;

      self.accumulator += delta;

      var steps = 0;
      while (self.accumulator >= STEP_MS && steps < MAX_STEPS) {
        self.tick();
        self.accumulator -= STEP_MS;
        steps += 1;

        /* tick() can end the run. Anything after that would be simulating a
           dead game. */
        if (!self.running) return;
      }

      /* Hit the ceiling: throw the backlog away rather than carry a debt
         that never clears. */
      if (self.accumulator > STEP_MS * MAX_STEPS) self.accumulator = 0;

      self.frame = window.requestAnimationFrame(step);
    };

    window.cancelAnimationFrame(this.frame);
    this.frame = window.requestAnimationFrame(step);
  };

  Racer.prototype.stop = function () {
    this.running = false;
    window.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.sound.stopEngine();
  };

  /* Everything down, including the music. Used when the modal closes — a
     shop page playing music at a tab nobody is looking at is the same
     mistake as autoplaying it in the first place. */
  Racer.prototype.silence = function () {
    this.sound.stopEngine();
    this.sound.stop('music');
  };

  Racer.prototype.pause = function () {
    if (!this.running) return;
    this.stop();
    this.status('paused');
  };

  /* ---------------------------------------------------------------- reward */

  Racer.prototype.remembered = function () {
    try {
      return window.localStorage.getItem('racer:earned') === '1';
    } catch (error) {
      return false;
    }
  };

  Racer.prototype.remember = function () {
    try {
      window.localStorage.setItem('racer:earned', '1');
    } catch (error) {
      /* Private mode. They keep the code for this session either way. */
    }
  };

  /* ---------------------------------------------------------------- reward */

  Racer.prototype.text = function (key, fallback) {
    return this.root.getAttribute('data-racer-text-' + key) || fallback;
  };

  /* Best, never the run that just ended: a bad run must not take away a
     reward already earned. */
  Racer.prototype.bestScore = function () {
    return Math.max(this.score, this.best);
  };

  Racer.prototype.won = function () {
    return this.rewardScore > 0 && this.bestScore() >= this.rewardScore;
  };

  Racer.prototype.show = function (node, on) {
    if (node) node.hidden = !on;
  };

  /* Everything the game-over panel says about the reward. Called after the
     score readout is filled in, so it only owns the reward lines. */
  Racer.prototype.renderResult = function () {
    var r = this.result;
    if (!r.tier) return;

    var best = this.bestScore();
    var won = this.won();
    var percent = String(this.rewardPercent);

    if (won) {
      r.tier.textContent = this.text('unlocked', 'YOU UNLOCKED [percent]% OFF')
        .replace('[percent]', percent);
    } else {
      r.tier.textContent = this.text('nothing', 'NO DISCOUNT YET');
    }

    this.show(r.tier, true);

    /* The nudge is the reason anyone taps play again, so it gets its own
       line rather than being folded into the tier text. */
    if (!won) {
      var togo = Math.max(1, this.rewardScore - best);
      var line = this.text('nudge', '[n] MORE DODGES TO UNLOCK [percent]% OFF')
        .replace('[n]', String(togo))
        .replace('[percent]', percent);

      /* "1 MORE DODGES" is the kind of small wrongness that makes a polished
         thing look unfinished. */
      if (togo === 1) line = line.replace(/DODGES/g, 'DODGE').replace(/dodges/g, 'dodge');

      r.nudge.textContent = line;
      this.show(r.nudge, true);
    } else {
      r.nudge.textContent = this.text('maxed', 'MAX DISCOUNT UNLOCKED');
      this.show(r.nudge, true);
    }

    var isCarGod = this.carGodScore > 0 && best >= this.carGodScore;
    if (r.carGod) {
      r.carGod.textContent =
        this.root.getAttribute('data-racer-cargod-label') || 'CAR GOD STATUS UNLOCKED';
      this.show(r.carGod, isCarGod);
    }

    /* Early access is promised to everyone who registers, not only to
       players who beat it, so this line is not gated on winning. */
    this.show(r.early, true);

    /* The form only appears once it has been earned. Before that the button
       would promise something the player cannot yet collect. */
    this.show(r.claim, won && !!r.form);
    this.show(r.form, false);
  };

  /* Fallback for when the clipboard is unavailable: highlight the code so a
     long-press or Cmd-C still gets it. */
  Racer.prototype.selectCode = function () {
    if (!this.reward.code || !window.getSelection || !document.createRange) return;

    var range = document.createRange();
    range.selectNodeContents(this.reward.code);

    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  Racer.prototype.revealReward = function () {
    this.rewarded = true;

    if (this.reward.code) this.reward.code.textContent = this.rewardCode;
    if (!this.reward.panel) return;

    this.reward.panel.hidden = false;

    /* Moved into view rather than left to be scrolled to. On a phone the
       panel opens below the fold as often as not, and a reward nobody sees
       is worse than no reward. */
    if (this.reward.panel.scrollIntoView) {
      this.reward.panel.scrollIntoView({ block: 'nearest' });
    }
  };

  /* Fired the moment the goal is passed. The game pauses rather than ends:
     hitting the target is a good thing, so taking the run away as a
     consequence would read as a punishment. */
  Racer.prototype.earn = function () {
    if (this.rewarded || !this.rewardCode) return;

    this.stop();
    this.remember();
    this.revealReward();
    this.status('goal');
    this.sound.play('reward');

    if (this.startButton) {
      this.startButton.textContent =
        this.root.getAttribute('data-racer-text-continue') || 'Keep playing';
    }
  };

  Racer.prototype.crash = function () {
    this.livesLeft -= 1;
    this.traffic = [];
    this.sound.play('crash');

    /* A moment of grace after a hit, or the respawn drops you straight into
       the car behind and takes a second life for one mistake. */
    this.invulnerable = 70;
    this.spawnIn = 54;
    this.report();

    if (this.livesLeft <= 0) this.gameOver();
  };

  Racer.prototype.gameOver = function () {
    this.stop();
    this.over = true;

    var record = this.score > this.best;
    if (record) {
      this.best = this.score;
      this.saveBest();
      this.report();
    }

    this.status(record ? 'record' : 'over');

    if (this.startButton) {
      this.startButton.textContent =
        this.root.getAttribute('data-racer-text-again') || 'Play again';
    }

    if (!this.panel.root) return;

    if (this.panel.score) this.panel.score.textContent = String(this.score);
    if (this.panel.best) this.panel.best.textContent = String(this.best);

    /* The "new best" line is the reason to play again, so it earns its own
       moment rather than being folded into the score readout. */
    if (this.panel.record) this.panel.record.hidden = !record;

    this.renderResult();

    this.panel.root.hidden = false;

    /* Focus moves to the play-again button so a keyboard player is not left
       tabbing to find it, and a screen reader lands on the panel. */
    if (this.panel.again && this.panel.again.focus) {
      this.panel.again.focus({ preventScroll: true });
    }
  };

  /* ----------------------------------------------------------------- loop */

  Racer.prototype.tick = function () {
    this.scroll += this.speed;
    if (this.invulnerable > 0) this.invulnerable -= 1;

    this.spawnIn -= 1;
    if (this.spawnIn <= 0) this.spawn();

    /* Must match render() exactly. If the hitbox and the drawn car disagree
       by even a few pixels the game feels rigged, and it is the kind of bug
       nobody can describe — they just say it "feels off". */
    var playerY = PLAYER_Y;

    for (var i = this.traffic.length - 1; i >= 0; i--) {
      var car = this.traffic[i];
      car.y += this.speed;

      /* Scored on the way past rather than on the way out, so the point
         lands at the moment it feels earned. */
      if (!car.passed && car.y > playerY + CAR_HEIGHT) {
        car.passed = true;
        this.score += 1;
        this.report();

        if (this.rampEvery > 0 && this.score % this.rampEvery === 0) {
          this.speed += 0.16;
          this.reportThrottle();
        }

        if (this.goal > 0 && this.score >= this.goal && !this.rewarded) {
          this.earn();
          /* earn() stopped the loop; finish this frame and let it stop. */
          this.render();
          return;
        }
      }

      if (car.y > WORLD_HEIGHT) {
        this.traffic.splice(i, 1);
        continue;
      }

      /* Overlap of the two cars as DRAWN, both measured from their centres,
         so the test matches what the player can see at any sprite scale.

         The forgiveness inset stays: a collision on the very tip of a bumper
         reads as unfair even when it is technically a touch. */
      if (this.invulnerable <= 0 && car.lane === this.lane) {
        var playerMid = playerY + CAR_HEIGHT / 2;
        var carMid = car.y + CAR_HEIGHT / 2;

        /* Measured from the sprites as fitted, so letterboxing inside a
           box never counts as bodywork. Placeholder cars fill their box
           exactly, so this is unchanged for them. */
        var reach =
          this.player.fittedHeight(this.playerBoxW, this.playerBoxH) / 2 +
          this.enemy.fittedHeight(this.enemyBoxW, this.enemyBoxH) / 2 - 4;

        if (Math.abs(carMid - playerMid) < reach) {
          this.crash();
          break;
        }
      }
    }

    this.render();
  };

  Racer.prototype.spawn = function () {
    /* Never fill every lane at once: a wall with no gap is not difficulty,
       it is a coin flip the player cannot win. */
    var blocked = [];
    for (var i = 0; i < this.traffic.length; i++) {
      if (this.traffic[i].y < CAR_HEIGHT * 2.1) blocked.push(this.traffic[i].lane);
    }

    var open = [];
    for (var lane = 0; lane < this.lanes; lane++) {
      if (blocked.indexOf(lane) === -1) open.push(lane);
    }

    if (open.length > 1) {
      var pick = open[Math.floor(Math.random() * open.length)];
      this.traffic.push({
        lane: pick,
        y: -CAR_HEIGHT,
        frame: Math.floor(Math.random() * Math.max(1, this.enemy.frames)),
        palette: PALETTES[1 + Math.floor(Math.random() * (PALETTES.length - 1))],
        passed: false
      });
    }

    this.spawnIn = this.gapFrames();
  };

  /* How long to wait before the next car, so that the empty road between
     them is the distance we want rather than whatever the speed happens to
     produce. */
  /* Linear interpolation between the GAP_CURVE anchors. Linear rather than
     eased on purpose: an eased curve makes the difficulty change hardest
     exactly where the player is least able to tell why, and this has to be
     tunable by someone reading the table, not solving for a spline. */
  /* 0 at the starting speed, 1 at roughly double it — which is around score
     80, where the gap has already bottomed out and speed is the only thing
     still rising. */
  Racer.prototype.reportThrottle = function () {
    var span = this.baseSpeed * 1.1;
    this.sound.throttle(span > 0 ? (this.speed - this.baseSpeed) / span : 0);
  };

  Racer.prototype.gapCarHeights = function (score) {
    var curve = GAP_CURVE;

    if (score <= curve[0][0]) return curve[0][1];

    for (var i = 1; i < curve.length; i++) {
      if (score <= curve[i][0]) {
        var from = curve[i - 1];
        var to = curve[i];
        var t = (score - from[0]) / (to[0] - from[0]);

        return from[1] + (to[1] - from[1]) * t;
      }
    }

    return GAP_MIN;
  };

  Racer.prototype.gapFrames = function () {
    var gap = Math.max(GAP_MIN, this.gapCarHeights(this.score));
    return Math.max(12, Math.round(gap * CAR_HEIGHT / this.speed));
  };

  /* --------------------------------------------------------------- render */

  /* The road, verge, kerbs and lane markings never change — only their
     vertical offset does. So they are drawn once into an offscreen tile and
     blitted a handful of times per frame.

     This is not a micro-optimisation. The dithering below touches roughly
     30,000 two-pixel cells; doing that per frame is thousands of fill calls
     sixty times a second and it visibly stutters on a mid-range phone. Drawn
     once, it costs nothing and the pattern is pixel-identical every frame,
     which is what stops the texture crawling as the road scrolls. */
  Racer.prototype.buildBackground = function () {
    var c = this.colors;
    var tile = document.createElement('canvas');
    tile.width = WORLD_WIDTH * this.scale;
    tile.height = TILE * this.scale;

    var ctx = tile.getContext('2d');
    /* Same trick as the main canvas: draw in world units into a bigger
       buffer, so the road is as sharp as everything else. */
    ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    ctx.imageSmoothingEnabled = !this.pixelArt;

    var x, y;
    var roadWidth = ROAD_RIGHT - ROAD_LEFT;

    /* Verge, with a dense two-tone checkerboard over it. A checker at this
       scale reads as grass texture; scattered dots read as litter. */
    ctx.fillStyle = c.grass;
    ctx.fillRect(0, 0, WORLD_WIDTH, TILE);

    ctx.fillStyle = shade(c.grass, -0.14);
    for (y = 0; y < TILE; y += 2) {
      for (x = 0; x < WORLD_WIDTH; x += 2) {
        if (((x >> 1) + (y >> 1)) % 2 === 0) ctx.fillRect(x, y, 2, 2);
      }
    }

    /* Asphalt, same treatment at lower contrast. */
    ctx.fillStyle = c.road;
    ctx.fillRect(ROAD_LEFT, 0, roadWidth, TILE);

    ctx.fillStyle = shade(c.road, -0.07);
    for (y = 0; y < TILE; y += 2) {
      for (x = ROAD_LEFT; x < ROAD_RIGHT; x += 2) {
        if (((x >> 1) + (y >> 1)) % 2 === 0) ctx.fillRect(x, y, 2, 2);
      }
    }

    /* A darker band down each side of the road. Real asphalt is dirtiest at
       the edges and the gradient inward stops the road reading as one flat
       grey rectangle — but this was 16 units of a 128-unit road, so a quarter
       of the driving surface was in shadow on each side and it read as the
       road being narrower than it is. Six is a weathered edge; sixteen was a
       second road surface. */
    var band = 6;
    ctx.fillStyle = shade(c.road, -0.1);
    ctx.fillRect(ROAD_LEFT, 0, band, TILE);
    ctx.fillRect(ROAD_RIGHT - band, 0, band, TILE);

    ctx.fillStyle = shade(c.road, -0.15);
    for (y = 0; y < TILE; y += 2) {
      for (x = 0; x < band; x += 2) {
        if (((x >> 1) + (y >> 1)) % 2 === 0) {
          ctx.fillRect(ROAD_LEFT + x, y, 2, 2);
          ctx.fillRect(ROAD_RIGHT - band + x, y, 2, 2);
        }
      }
    }

    /* Solid edge line inside each kerb. Real roads have one, and it gives the
       eye a crisp boundary between asphalt and kerb that the dither alone
       does not. */
    ctx.fillStyle = c.line;
    ctx.fillRect(ROAD_LEFT + 2, 0, 1, TILE);
    ctx.fillRect(ROAD_RIGHT - 3, 0, 1, TILE);

    /* Kerbs. Tall alternating blocks scrolling past are most of what sells
       the sense of speed — more than the lane dashes do.

       Five units wide, not ten. At ten they were 5% of the world each side
       and dominated the frame; the eye reads the kerb as trim, and trim that
       thick stops looking like pixel art and starts looking like a border. */
    var kerb = 5;

    ctx.fillStyle = '#8e1a1a';
    ctx.fillRect(ROAD_LEFT - kerb, 0, kerb, 26);
    ctx.fillRect(ROAD_RIGHT, 0, kerb, 26);

    ctx.fillStyle = '#e6e6e6';
    ctx.fillRect(ROAD_LEFT - kerb, 26, kerb, 26);
    ctx.fillRect(ROAD_RIGHT, 26, kerb, 26);

    /* One-unit contact shadow where the kerb meets the grass. */
    ctx.fillStyle = 'rgba(0,0,0,.18)';
    ctx.fillRect(ROAD_LEFT - kerb - 1, 0, 1, TILE);
    ctx.fillRect(ROAD_RIGHT + kerb, 0, 1, TILE);

    /* Lane dashes. Three units wide and 18 long against a 34 gap: the old
       5-wide, half-on-half-off dash was thicker than the edge line and read
       as a painted stripe rather than a marking. Dash plus gap still sums to
       TILE so the seam stays invisible. */
    ctx.fillStyle = c.line;
    for (var lane = 1; lane < this.lanes; lane++) {
      var lx = Math.round(ROAD_LEFT + roadWidth / this.lanes * lane - 1);
      ctx.fillRect(lx, 0, 3, 18);
    }

    this.background = tile;
    this.tileHeight = TILE;
  };

  /* A tree is three overlapping circles in a dark tone, the same shape inset
     in a mid tone, and a highlight blob offset up and left as if the sun is
     over the player's shoulder. Three tones and a consistent light direction
     is the whole difference between a green blob and a tree. */
  Racer.prototype.drawTree = function (x, y, r) {
    var ctx = this.ctx;
    var g = this.colors.grass;

    ctx.fillStyle = 'rgba(0,0,0,.18)';
    pixelCircle(ctx, x + 2, y + r - 1, r - 1);

    ctx.fillStyle = '#5a3d22';
    ctx.fillRect(Math.round(x - 2), Math.round(y + r - 6), 4, 9);

    ctx.fillStyle = shade(g, -0.34);
    pixelCircle(ctx, x, y, r);
    pixelCircle(ctx, x - r * 0.55, y + r * 0.30, r * 0.62);
    pixelCircle(ctx, x + r * 0.55, y + r * 0.28, r * 0.60);

    ctx.fillStyle = shade(g, -0.16);
    pixelCircle(ctx, x, y - 1, r - 3);
    pixelCircle(ctx, x - r * 0.52, y + r * 0.26, r * 0.44);
    pixelCircle(ctx, x + r * 0.52, y + r * 0.24, r * 0.42);

    ctx.fillStyle = shade(g, 0.14);
    pixelCircle(ctx, x - r * 0.28, y - r * 0.34, r * 0.34);
    pixelCircle(ctx, x + r * 0.22, y - r * 0.16, r * 0.20);
  };

  Racer.prototype.drawBush = function (x, y, r) {
    var ctx = this.ctx;
    var g = this.colors.grass;

    ctx.fillStyle = shade(g, -0.34);
    pixelCircle(ctx, x, y, r);
    pixelCircle(ctx, x - r * 0.6, y + r * 0.25, r * 0.6);
    pixelCircle(ctx, x + r * 0.6, y + r * 0.22, r * 0.58);

    ctx.fillStyle = shade(g, -0.16);
    pixelCircle(ctx, x, y - 1, r - 2);

    ctx.fillStyle = shade(g, 0.14);
    pixelCircle(ctx, x - r * 0.25, y - r * 0.3, r * 0.28);
  };

  /* A car seen from directly above: wheels proud of the body at all four
     corners, a stripe down the middle, dark glass front and rear, and a
     shadow offset down-right to match the tree lighting. */
  /* The placeholder car.

     Built as a SCANLINE SILHOUETTE rather than a stack of rectangles: each
     row gets its own inset, so the body can taper at the nose and round at
     the tail without a single curve being drawn. Stacked rectangles gave a
     symmetrical brick — readable, but it never looked like it was pointing
     anywhere, and direction is most of what makes a top-down car read as a
     car.

     Insets are asymmetric on purpose. The nose is sharper than the tail,
     which is what tells the eye which way the thing is facing at 32px wide. */
  function carInset(row, h) {
    if (row <= 0) return 8;
    if (row === 1) return 6;
    if (row === 2) return 5;
    if (row === 3) return 4;
    if (row === 4) return 3;
    if (row === 5) return 2;

    if (row >= h - 1) return 5;
    if (row >= h - 2) return 4;
    if (row >= h - 4) return 3;
    if (row >= h - 6) return 2;

    return 1;
  }

  Racer.prototype.drawCar = function (x, y, palette, mine) {
    var ctx = this.ctx;
    var w = CAR_WIDTH;
    var h = CAR_HEIGHT;
    var row, inset;

    /* Offset down and right, and no wider than the car. The old shadow was
       drawn wider than the body, which read as a dark halo rather than a
       car sitting on tarmac. */
    ctx.fillStyle = 'rgba(0,0,0,.28)';
    for (row = 0; row < h; row++) {
      inset = carInset(row, h);
      ctx.fillRect(x + inset + 2, y + row + 3, w - inset * 2, 1);
    }

    /* Wheels first, so the body overlaps them and they read as tucked into
       the arches rather than stuck on the side. */
    ctx.fillStyle = '#0f0f10';
    ctx.fillRect(x - 1, y + 9, 4, 11);
    ctx.fillRect(x + w - 3, y + 9, 4, 11);
    ctx.fillRect(x - 1, y + h - 21, 4, 12);
    ctx.fillRect(x + w - 3, y + h - 21, 4, 12);

    /* Outline, then the body inset one pixel inside it. One dark pixel all
       the way round is what separates the car from the asphalt — without it
       a dark car on a dark road loses its edges entirely. */
    ctx.fillStyle = palette[2];
    for (row = 0; row < h; row++) {
      inset = carInset(row, h);
      ctx.fillRect(x + inset, y + row, w - inset * 2, 1);
    }

    ctx.fillStyle = palette[0];
    for (row = 1; row < h - 1; row++) {
      inset = carInset(row, h) + 1;
      ctx.fillRect(x + inset, y + row, w - inset * 2, 1);
    }

    /* Highlight down the middle of the bodywork, stopping short of both
       ends so the nose and tail stay in the darker base tone. */
    ctx.fillStyle = palette[1];
    for (row = 6; row < h - 7; row++) {
      inset = carInset(row, h) + 4;
      ctx.fillRect(x + inset, y + row, w - inset * 2, 1);
    }

    /* Racing stripes. Two narrow, one wide down the centre line. */
    ctx.fillStyle = palette[3];
    ctx.fillRect(x + w / 2 - 1, y + 4, 2, h - 8);
    ctx.fillStyle = palette[0];
    ctx.fillRect(x + w / 2 - 5, y + 5, 2, h - 10);
    ctx.fillRect(x + w / 2 + 3, y + 5, 2, h - 10);

    /* Glass. The lighter band along the top edge of the windscreen is a
       reflection, and it is the single detail that stops the windows reading
       as holes cut in the roof. */
    ctx.fillStyle = '#0d1420';
    ctx.fillRect(x + 6, y + 11, w - 12, 10);
    ctx.fillStyle = '#22344a';
    ctx.fillRect(x + 6, y + 11, w - 12, 2);

    ctx.fillStyle = '#0d1420';
    ctx.fillRect(x + 6, y + h - 21, w - 12, 9);
    ctx.fillStyle = '#1a2838';
    ctx.fillRect(x + 6, y + h - 21, w - 12, 2);

    /* Roof between the two windows. */
    ctx.fillStyle = palette[1];
    ctx.fillRect(x + 6, y + 22, w - 12, h - 44);

    /* Mirrors. Two pixels each, and they do a surprising amount of work. */
    ctx.fillStyle = palette[2];
    ctx.fillRect(x + 1, y + 20, 2, 3);
    ctx.fillRect(x + w - 3, y + 20, 2, 3);

    /* Every car, not only the player's. They all face the same way, so they
       all have a nose — a car with taillights and no headlights reads as
       unfinished next to one that has both. */
    ctx.fillStyle = mine ? '#fff6c8' : '#f4ead0';
    ctx.fillRect(x + 7, y + 1, 4, 2);
    ctx.fillRect(x + w - 11, y + 1, 4, 2);

    ctx.fillStyle = '#ff4a3d';
    ctx.fillRect(x + 6, y + h - 3, 5, 2);
    ctx.fillRect(x + w - 11, y + h - 3, 5, 2);
  };

  Racer.prototype.render = function () {
    var ctx = this.ctx;
    var i;
    var tile = this.tileHeight;
    var offset = Math.floor(this.scroll) % tile;

    /* One extra tile above and below so a partial tile never leaves a gap at
       either edge as the offset moves. */
    for (i = -tile; i < WORLD_HEIGHT + tile; i += tile) {
      ctx.drawImage(this.background, 0, i + offset, WORLD_WIDTH, tile);
    }

    for (i = 0; i < this.scenery.length; i++) {
      var item = this.scenery[i];

      /* Wrapped over a range taller than the canvas, so a tree leaves the
         bottom of the screen completely before it reappears at the top. */
      var sy = ((item.y + this.scroll) % (WORLD_HEIGHT + 80)) - 40;

      if (this.scenerySheet.ready) {
        this.scenerySheet.draw(
          ctx, item.frame,
          item.x - this.sceneryBox / 2,
          sy - this.sceneryBox / 2,
          this.sceneryBox, this.sceneryBox
        );
      } else if (item.kind === 'tree') {
        this.drawTree(item.x, sy, item.r);
      } else {
        this.drawBush(item.x, sy, item.r * 0.62);
      }
    }

    for (i = 0; i < this.traffic.length; i++) {
      var car = this.traffic[i];
      var cx = this.laneX(car.lane);

      /* Grown from the centre of the hitbox, not the corner, so the car
         stays in its lane as it scales. */
      var ew = CAR_WIDTH * this.enemyScale;
      var eh = CAR_HEIGHT * this.enemyScale;

      if (!this.enemy.draw(
        ctx, car.frame,
        cx - (ew - CAR_WIDTH) / 2,
        car.y - (eh - CAR_HEIGHT) / 2,
        ew, eh
      )) {
        this.drawCar(cx, Math.round(car.y), car.palette, false);
      }
    }

    /* Blink while invulnerable so the state is visible rather than just
       felt. */
    var hidden = this.invulnerable > 0 && Math.floor(this.invulnerable / 4) % 2;
    if (!hidden) {
      var px = this.laneX(this.lane);
      var pw = CAR_WIDTH * this.playerScale;
      var ph = CAR_HEIGHT * this.playerScale;

      if (!this.player.draw(
        ctx, 0,
        px - (pw - CAR_WIDTH) / 2,
        PLAYER_Y - (ph - CAR_HEIGHT) / 2,
        pw, ph
      )) {
        this.drawCar(px, PLAYER_Y, PALETTES[0], true);
      }
    }
  };

  /* ======================================================================
     BOOT
     ====================================================================== */

  function boot() {
    document.querySelectorAll('[data-racer]').forEach(function (root) {
      if (root.racer) return;
      root.racer = new Racer(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify re-renders a section when its settings change in the editor, so
     the game has to come back with it. */
  document.addEventListener('shopify:section:load', boot);
})();
